import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AccentFoldService, AppService, CoreTypes, LabelValue } from 'common-cloudstudio';

interface TableData {
  Elements: Element[];
  Columns: ColumnDefinition[];
}

interface TableAction {
  Icon?: string;
  Tooltip?: string;
  Action: (row: any) => void;
}
interface CustomTableAction {
  ActionType: 'checkbox' | 'icon-text' | 'text',
  Checked?: false,
  Text?: string,
  Icon?: string;
  Tooltip?: string;
  Action: (row: any) => void;
}

interface ColumnDefinition {
  Title: string;
  Field: string;
  Alignment?: CoreTypes.TableColumnAlignment;
  Type?: string;
  CellType?: 'checkbox' | 'icon-text' | 'text';
  IconClass?: string;
  TextField?: string;
  Actions?: TableAction[];
  CustomActions?: CustomTableAction[];
  WidthPixels?: number;
}

interface CellContent {
  Type: 'checkbox' | 'icon-text' | 'text';
  Text?: string;
  IconClass?: string;
  Actions?: TableAction[];
  Checked?: boolean;
}

interface Element {
  ElementId: number;             
  CustomCols?: CellContent[];
  RowStyle?: { [key: string]: any }; 
  [key: string]: any;
}

@Component({
  selector: 'app-custom-table',
  templateUrl: './custom-table.component.html',
  styleUrls: ["./custom-table.component.scss"]
})
export class CustomTableComponent implements OnInit, OnChanges  {
  @ViewChild('customTableCmp') customTable: ElementRef;
  @Input() data:any;
  @Input() itemsPerPageOptions = [10, 25, 50, 100];
  @Input() disableFilter: boolean = false;
  @Input() enablePagination: boolean = false;
  @Input() enableRowsPerPage: boolean = false;
  @Input() allowNewRows: boolean = false;
  @Input() loadData: boolean = false;
  @Input() enableRadioSelected: boolean = false;
  @Input() enableCheckSelected: boolean = false;
  @Input() enableWarningTooltip: boolean = false;
  @Input() warningTooltipText: boolean = false;
  @Input() menuButtonList: LabelValue<any>[] = [];

  @Output() onAddNewRows =  new EventEmitter();
  @Output() onSelectedRow =  new EventEmitter();
  @Output() onSelectedRowList =  new EventEmitter();
 
  fileNumber = Math.floor(1000 + Math.random() * 9000);

  itemsPerPage = this.itemsPerPageOptions[0];
  currentPage = 1;
  searchText = '';

  filteredData: Element[] = null;
  paginatedData: Element[] = null;
  totalPages: number = 1;
  pageNumbers: number[] = [];
  actionsColumn?: ColumnDefinition;
  
  alignTypes = {
    Left: CoreTypes.TableColumnAlignment.Left,
    Right: CoreTypes.TableColumnAlignment.Right,
    Center: CoreTypes.TableColumnAlignment.Center
  };
  sort: {column: number, direction: number} =  { column: -1, direction: null as null | 1 | 2 };
  selectedRow: number | null = null;
  selectedChek: boolean | null = null;
  selectAllElements: boolean = false;
  selectedRowElements: any[] = [];
  selectedRows: any[] = [];

  
  constructor(
    public appService: AppService,
    private accentFoldService: AccentFoldService
  ) {}

  ngOnInit() {
    // this.processData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.data) {
      this.processData();
    }
  }

  processData() {
    if (this.searchText) {
      const lowerSearch = this.accentFoldService.accentFold(this.searchText.toLowerCase());
      this.filteredData = this.data?.Elements?.filter(row => this.containsSearchText(row, lowerSearch));
    } else {
      this.filteredData = this.data?.Elements?.length ? [...this.data?.Elements] : [];
    }
  
    if (this.sort.column !== -1) {
      this.sortTable(this.sort.column, false);
    }
    this.sort.direction = null;
    this.totalPages = Math.ceil(this.filteredData.length / this.itemsPerPage) || 1;
    this.pageNumbers = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    this.paginatedData = this.filteredData.slice((this.currentPage - 1) * this.itemsPerPage, this.currentPage * this.itemsPerPage);
    this.actionsColumn = this.data.Columns.find(col => col.Field === 'actions');
  }
  
  containsSearchText(value: any, searchText: string): boolean {
    if (value === null || value === undefined) { return false; }
    if (typeof value === 'object') {
      return Object.values(value).some(innerValue => this.containsSearchText(innerValue, searchText));
    } else {
      return value.toString().toLowerCase().includes(searchText);
    }
  }
  
  sortTable(columnIndex: number, toggleDirection: boolean = true) {
    if (toggleDirection) {
      if (this.sort.column === columnIndex) {
        this.sort.direction = this.sort.direction === 1 ? 2 : (this.sort.direction === 2 ? null : 1);
      } else {
        this.sort.column = columnIndex;      
        this.sort.direction = 1;
      }
    }
    if (this.sort.direction === null) {
      this.filteredData = [...this.data?.Elements];
    } else {
      const column = this.data.Columns[this.sort.column];
      const direction = this.sort.direction === 1 ? 1 : -1;
      this.filteredData.sort((a, b) => {
        if (a[column.Field] < b[column.Field]) return -direction;
        if (a[column.Field] > b[column.Field]) return direction;
        return 0;
      });
    }
    this.paginatedData = this.filteredData.slice((this.currentPage - 1) * this.itemsPerPage, this.currentPage * this.itemsPerPage);
  }

  cleanFilter() {
    this.searchText = '';
    this.currentPage = 1;
    this.processData();
  }

  onPageChange(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.paginatedData = this.filteredData.slice((this.currentPage - 1) * this.itemsPerPage, this.currentPage * this.itemsPerPage);
  }

  onItemsPerPageChange(event: any) {
    this.itemsPerPage = +event.target.value;
    this.currentPage = 1;
    this.processData();
  }

  onSearchChange() {
    this.currentPage = 1;
    this.processData();
  }

  addNewRows() {
    this.onAddNewRows.emit(true);
  }

  onRowSelect(rowIndex: number): void {
    this.selectedRow = rowIndex;
    
    this.onSelectedRow.emit(this.selectedRow);
  }

  cleanRowSelected() {
    this.selectedRow = null;
  }

  toggleSelectAll(event: Event) {
    this.selectedRowElements = [];
    this.data?.Elements?.forEach((row) => {
      row.Enabled = this.selectAllElements
      if (row.Enabled) {
        this.selectedRowElements.push(row);
      }
    });
    this.onSelectedRowList.emit(this.selectedRowElements);
  }

  onCheckboxChange() {
    this.selectAllElements = false;
    const eligibleRows = this.data?.Elements?.filter((row) => !row?.Enabled );
    this.selectAllElements = eligibleRows.every((row) => row.Enabled);
    this.getElementList();
  }

  selectWithoutEvents() {
    this.data?.Elements?.forEach((item) => {
      item.Enabled = !item['Events']?.EventID;
    });
    this.selectAllElements = this.data?.Elements?.every((item) => item.Enabled);
    this.getElementList();
  }

  selectWithEvents() {
    this.data?.Elements?.forEach((item) => {
      item.Enabled = item['Events']?.EventID;
    });
    this.selectAllElements = this.data?.Elements?.every((item) => item.Enabled);
    this.getElementList();
  }

  getElementList() {
    const selectedElements = this.data?.Elements?.filter((row) => row?.Enabled);
    this.selectedRowElements = selectedElements;
    this.onSelectedRowList.emit(this.selectedRowElements);
  }

  dropdownMenuAction(event, item) {
    switch (item.value) {
      case 1:
        this.selectAllElements = true;
        this.toggleSelectAll(event);
        break;
      case 2:
        this.selectAllElements = false;
        this.toggleSelectAll(event);
        break;
      case 3:
        this. selectWithoutEvents()
        break;
      case 4:
        this.selectWithEvents()
        break;
      default:
        break;
    }
  }
}
